import React, {useContext, useEffect, useRef, useState} from "react";
import "./FullstackForm.scss";
import {CompanyContext} from "../../../../context/CompanyContext";
import {
    FullstackTokenizer,
    FullstackTokenizerResponse,
    FullstackTokenizerSettings, FullstackValidResponse
} from "../../../../context/FullstackContext";
import {PAYMENT_METHOD_TYPE} from "../AchSwitcher/AchSwitcher";
import {ToastService} from "../../../../service/ToastService";
import {CardholderNameInput} from "../CardholderNameInput/CardholderNameInput";
import {ZipCodeInput} from "../ZipCodeInput/ZipCodeInput";
import {PreloaderContext} from "../../../../context/PreloaderContext";
import {AmountInput} from "../AmountInput/AmountInput";
import {CONSTANTS} from "../../../../common/constants";
import {getFullstackPublicApiKey} from "../../../../api/FullstackApi";

interface FullstackFormProps {
    amount: string;
    setFullstackTokenizer: (fullstackTokenizer: FullstackTokenizer) => void;
    isAchAvailable: boolean;
    isCreditCardAvailable: boolean;
    onApprovedPayment: (token: string) => void;
    paymentMethodType: PAYMENT_METHOD_TYPE;
    cardholderName: string;
    setCardholderName: (cardholderName: string) => void;
    setIsCardholderNameValid: (isCardholderNameValid: boolean) => void;
    zipCode: string;
    setZipCode: (cardholderName: string) => void;
    setIsZipCodeValid: (isZipCodeValid: boolean) => void;
    setAmount: (amount: string) => void;
    setIsAmountValid: (isAmountValid: boolean) => void;
    onSetValidFullstack: (isFullstackValid: boolean) => void;
}

export const FullstackForm: React.FunctionComponent<FullstackFormProps> = (props: FullstackFormProps) => {
    const company = useContext(CompanyContext);
    const preloaderContext = useContext(PreloaderContext);
    const [publicApiKey, setPublicApiKey] = useState<string>();

    useEffect(() => {
        if (publicApiKey) {
            onInitFullstack(publicApiKey);
        } else {
            preloaderContext.showMiddlePagePreloader();
            getFullstackPublicApiKey()
                .then((publicApiKeyResponse) => {
                    setPublicApiKey(publicApiKeyResponse['public-key']);
                    onInitFullstack(publicApiKeyResponse['public-key']);
                })
        }

    }, [props.paymentMethodType]);

    const onInitFullstack = (publicApiKey: string) => {
        if (document.getElementById('fullstack-payment-container')?.innerHTML) {
            (document.getElementById('fullstack-payment-container') as HTMLElement).innerHTML = '';
        }
        initFullstack(publicApiKey);
    }

    const fullstackApproveCallback = (response: FullstackTokenizerResponse) => {
        if (response.status === 'success') {
            props.onApprovedPayment(response.token as string);
        } else if (response.status === 'validation' && response.invalid) {
            if (response.invalid.indexOf('cc') !== -1) {
                ToastService.middleScreenErrorToast(CONSTANTS.REMOTE_PAYMENT_ERRORS.TOKEN);
            } else if (response.invalid.indexOf('exp') !== -1) {
                ToastService.middleScreenErrorToast(CONSTANTS.REMOTE_PAYMENT_ERRORS.EXPIRATION);
            }
        } else {
            preloaderContext.hideMiddlePagePreloader();
            ToastService.middleScreenErrorToast(response.msg as string);
        }
    }

    const fullstackValidCallback = (response: FullstackValidResponse) => {
        props.onSetValidFullstack(response?.isValid || false);
    }

    const initFullstack = async (publicApiKey: string) => {
        const fullstackTokenizer = new (window as any).Tokenizer({
            apikey: publicApiKey,
            container: '#fullstack-payment-container',
            submission: fullstackApproveCallback,
            validCard: fullstackValidCallback,
            settings: {
                payment: {
                    types: [props.paymentMethodType === PAYMENT_METHOD_TYPE.ACH ? 'ach' : 'card'],
                    ach: {billing_required: {zip: false}},
                    card: {billing_required: {zip: false}},
                },

                styles: {
                    'input': {
                        'color': '#5c5c5c',
                        'border': '1px solid rgb(1, 2, 77)',
                        'border-radius': '3px',
                        'height': '40px',
                        'font-size': '16px'
                    },
                    'select': {
                        'color': '#5c5c5c',
                        'border': '1px solid rgb(1, 2, 77)',
                        'border-radius': '3px',
                        'height': '40px',
                        'font-size': '16px'
                    },
                    '.fieldset': {
                        'margin-bottom': '15px'
                    }
                }
            }
        } as FullstackTokenizerSettings) as FullstackTokenizer;
        props.setFullstackTokenizer(fullstackTokenizer);
        preloaderContext.hideMiddlePagePreloader();
    }

    return <div>
        <div className="form-group">
            <div className='input-container'>
                <label>Amount ({company.currency_code}):</label>
                <AmountInput amount={props.amount} maxAmount={props.amount} onSetAmount={props.setAmount}
                             onSetIsAmountValid={props.setIsAmountValid}/>
            </div>
        </div>
        <div id="fullstack-payment-container"/>
        <div className="form-group">
            <div className='input-container'>
                <label>{props.paymentMethodType === PAYMENT_METHOD_TYPE.ACH ? 'Account Holder Name' : 'Cardholder Name:'}</label>
                <CardholderNameInput cardholderName={props.cardholderName}
                                     onSetCardholderName={props.setCardholderName}
                                     onSetIsCardholderNameValid={props.setIsCardholderNameValid}/>
            </div>

            <div className='input-container'>
                <label>ZIP:</label>
                <ZipCodeInput zipCode={props.zipCode} onSetZipCode={props.setZipCode}
                              onSetZipCodeValid={props.setIsZipCodeValid}/>
            </div>
        </div>

    </div>
}
